// A patient's history as a tree: one row per day and program (the latest open), whose
// arrow opens the sessions run that day by their start time, whose arrows open the tests
// by the time each was run, with its result. Admins get tick boxes to delete sessions or
// single tests (with the shared delete bar).

import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import type { SessionInfo, TestInfo } from "../api";
import { resultText } from "../lib/results";
import { AllCheck, BulkBar, RowCheck, rowKey, useSelection } from "./Bulk";

const EYE_SHORT: Record<string, string> = { OD: "R", OS: "L", OU: "both" };
const EYE: Record<string, string> = { OD: "Right eye", OS: "Left eye", OU: "Both eyes" };
const PURPOSE: Record<string, string> = { repeat: "repeat", confirm: "check of a change" };

interface Group { key: string; day: string; dayLabel: string; program: string; sessions: SessionInfo[] }

function localDay(iso: string): string {
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
const dayLabel = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short", year: "numeric" });
const time = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" }) : "—";
const programName = (s: SessionInfo) => (s.protocol as { name?: string } | null)?.name || "no program";
const isDemo = (s: SessionInfo) => !!s.qualification?.demo;

function testsInOrder(s: SessionInfo): TestInfo[] {
  return [...s.tests].sort((a, b) => (a.started_at ?? a.scored_at ?? "9").localeCompare(b.started_at ?? b.scored_at ?? "9")
    || a.order - b.order || a.id - b.id);
}

/** The counted results of a day, per test: "R 0.10 · L 0.14 logMAR". */
function daySummary(sessions: SessionInfo[]): string[] {
  const latest = new Map<string, { eye: string; value: string }>();
  for (const s of sessions) {
    for (const t of testsInOrder(s)) {
      const r = resultText(t);
      if (!r?.counted || !r.value || t.result?.demo) continue;     // debug (checks skipped) results are not part of the record
      latest.set(`${t.kind}|${t.eye}`, { eye: t.eye, value: r.value });
    }
  }
  // "0.10 logMAR" -> ["0.10", "logMAR"]; "below 0.00 log CS" -> ["<0.00", "log CS"]
  const split = (v: string): [string, string] => {
    const m = /^(below )?(-?[\d.]+) ?(.*)$/.exec(v);
    return m ? [`${m[1] ? "<" : ""}${m[2]}`, m[3] ?? ""] : [v, ""];
  };
  const out: string[] = [];
  for (const kind of ["acuity", "contrast"]) {
    const items = [...latest.entries()].filter(([k]) => k.startsWith(kind + "|")).map(([, v]) => ({ eye: v.eye, parts: split(v.value) }));
    if (!items.length) continue;
    const unit = items[0]!.parts[1];
    const same = items.every((i) => i.parts[1] === unit);
    const vals = items.map((i) => `${EYE_SHORT[i.eye] ?? i.eye} ${same ? i.parts[0] : i.parts.join(" ")}`).join(" · ");
    out.push(`${kind === "acuity" ? "Acuity" : "CS"} ${vals}${same && unit ? ` ${unit}` : ""}`);
  }
  return out;
}

export function HistoryTree({ sessions, canRun, isAdmin, onChanged }: {
  sessions: SessionInfo[]; canRun: boolean; isAdmin: boolean; onChanged: () => void;
}) {
  const groups = useMemo(() => {
    const m = new Map<string, Group>();
    for (const s of sessions) {
      const day = localDay(s.started_at);
      const key = `${day}|${programName(s)}`;
      const g = m.get(key) ?? { key, day, dayLabel: dayLabel(s.started_at), program: programName(s), sessions: [] };
      g.sessions.push(s);
      m.set(key, g);
    }
    for (const g of m.values()) g.sessions.sort((a, b) => a.started_at.localeCompare(b.started_at));
    return [...m.values()].sort((a, b) => b.day.localeCompare(a.day) || a.program.localeCompare(b.program));
  }, [sessions]);
  const [open, setOpen] = useState<Set<string>>(() => new Set(groups[0] ? [`g:${groups[0].key}`] : []));
  const sel = useSelection();

  const toggle = (k: string) => setOpen((prev) => {
    const next = new Set(prev);
    if (next.has(k)) next.delete(k); else next.add(k);
    return next;
  });
  const expandAll = () => setOpen(new Set([...groups.map((g) => `g:${g.key}`), ...sessions.map((s) => `s:${s.id}`)]));
  const collapseAll = () => setOpen(new Set());

  // rows in the order shown, for shift-click ranges
  const order: string[] = [];
  for (const g of groups) {
    if (!open.has(`g:${g.key}`)) continue;
    for (const s of g.sessions) {
      order.push(rowKey("sessions", s.id));
      if (open.has(`s:${s.id}`)) for (const t of testsInOrder(s)) order.push(rowKey("tests", t.id));
    }
  }
  const allSessions = sessions.map((s) => rowKey("sessions", s.id));

  if (!sessions.length) return <p className="muted">No sessions yet.</p>;
  return (
    <>
      <div className="row tree-tools">
        <button className="link small" onClick={expandAll}>Expand all</button>
        <button className="link small" onClick={collapseAll}>Collapse all</button>
      </div>
      {isAdmin && (
        <BulkBar sel={sel} shown={allSessions} what="sessions or single tests" onDeleted={onChanged} presets={[
          { label: "Debug sessions", keys: sessions.filter(isDemo).map((s) => rowKey("sessions", s.id)) },
          { label: "Unfinished sessions", keys: sessions.filter((s) => s.status !== "complete").map((s) => rowKey("sessions", s.id)) },
          { label: "Stopped or invalid tests", keys: sessions.flatMap((s) => s.tests.filter((t) => t.result && !resultText(t)?.counted)
            .map((t) => rowKey("tests", t.id))) },
        ]} />
      )}
      <table className="tree" role="treegrid" aria-label="History">
        <thead>
          <tr>
            {isAdmin && <th className="pick-col"><AllCheck sel={sel} keys={allSessions} label="Select every session" /></th>}
            <th>Date</th><th>Program</th><th>What</th><th>Result</th><th />
          </tr>
        </thead>
        <tbody>
          {groups.map((g) => {
            const gOpen = open.has(`g:${g.key}`);
            const nTests = g.sessions.reduce((n, s) => n + s.tests.length, 0);
            const modes = [...new Set(g.sessions.map((s) => s.mode))].join(" + ");
            const label = `${g.dayLabel} ${g.program}`;
            return [
              <tr key={g.key} className="tree-day" aria-level={1} aria-expanded={gOpen}>
                {isAdmin && <td className="pick-col">
                  <AllCheck sel={sel} keys={g.sessions.map((s) => rowKey("sessions", s.id))} label={`Select ${label}`} /></td>}
                <td className="tree-label">
                  <button className="twisty" aria-expanded={gOpen} aria-label={`${gOpen ? "Collapse" : "Expand"} ${label}`}
                    onClick={() => toggle(`g:${g.key}`)}>▸</button>{g.dayLabel}
                </td>
                <td className="tree-program">{g.program}</td>
                <td className="tree-what">{g.sessions.length === 1 ? "1 session" : `${g.sessions.length} sessions`} · {nTests} test{nTests === 1 ? "" : "s"} · {modes}
                  {g.sessions.some(isDemo) && <> <span className="badge demo">debug</span></>}
                  {g.sessions.some((s) => s.status === "in_progress") && <> <span className="badge">in progress</span></>}</td>
                <td className="tree-result">{daySummary(g.sessions).map((l) => <div key={l}>{l}</div>)}</td>
                <td />
              </tr>,
              ...(gOpen ? g.sessions.flatMap((s) => {
                const sKey = rowKey("sessions", s.id);
                const sOpen = open.has(`s:${s.id}`);
                const sPicked = sel.has(sKey);
                const tests = testsInOrder(s);
                const version = (s.protocol as { version?: number } | null)?.version;
                return [
                  <tr key={`s${s.id}`} className={`tree-session ${sPicked ? "picked" : ""}`} aria-level={2} aria-expanded={sOpen}>
                    {isAdmin && <td className="pick-col"><RowCheck sel={sel} k={sKey} order={order} label={`Select session at ${time(s.started_at)} on ${g.dayLabel}`} /></td>}
                    <td className="tree-label lvl2">
                      <button className="twisty" aria-expanded={sOpen} aria-label={`${sOpen ? "Collapse" : "Expand"} session at ${time(s.started_at)}`}
                        disabled={!tests.length} onClick={() => toggle(`s:${s.id}`)}>▸</button>
                      <time dateTime={s.started_at}>{time(s.started_at)}</time>
                      {s.completed_at && <span className="muted"> – {time(s.completed_at)}</span>}
                    </td>
                    <td className="muted small">{version ? `version ${version}` : ""}</td>
                    <td>{s.mode} · <span className={`badge ${s.calibration_tier}`}>{s.calibration_tier}</span> · {s.status === "in_progress" ? "in progress" : s.status}
                      {isDemo(s) && <> <span className="badge demo">debug</span></>}</td>
                    <td className="muted small">{tests.length} test{tests.length === 1 ? "" : "s"}</td>
                    <td>{s.status === "in_progress" && canRun
                      ? <Link to={`/sessions/${s.id}/run`}>Continue</Link>
                      : <Link to={`/sessions/${s.id}`}>Results</Link>}</td>
                  </tr>,
                  ...(sOpen ? tests.map((t) => {
                    const r = resultText(t);
                    const tKey = rowKey("tests", t.id);
                    return (
                      <tr key={`t${t.id}`} className={`tree-test ${sel.has(tKey) || sPicked ? "picked" : ""}`} aria-level={3}>
                        {isAdmin && <td className="pick-col"><RowCheck sel={sel} k={tKey} order={order} disabled={sPicked}
                          checked={sPicked || sel.has(tKey)} label={`Select ${EYE[t.eye] ?? t.eye} ${t.kind} at ${time(t.started_at)}`} /></td>}
                        <td className="tree-label lvl3">
                          <time dateTime={t.started_at ?? undefined}>{time(t.started_at ?? t.scored_at)}</time>
                          {t.started_at && t.scored_at && <span> – {time(t.scored_at)}</span>}
                        </td>
                        <td />
                        <td>{EYE[t.eye] ?? t.eye} · {t.kind === "acuity" ? "acuity" : "contrast sensitivity"}
                          {t.purpose && PURPOSE[t.purpose] && <> <span className="badge">{PURPOSE[t.purpose]}</span></>}</td>
                        <td>{r ? (r.counted && r.value ? <>{r.value} <span className="muted">{r.detail}</span></> : <span className="warn">{r.detail}</span>)
                          : <span className="muted">{t.status === "pending" ? "not run yet" : t.status}</span>}</td>
                        <td />
                      </tr>
                    );
                  }) : []),
                ];
              }) : []),
            ];
          })}
        </tbody>
      </table>
    </>
  );
}
